import React, { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { DATESYSTEM_TO_FORMAT } from '../../pages/subpages/Settings';
import { useAppStore } from '../../shared/stores/useAppStore';
import snakeToPascal, { revertLocaleString, round } from '../../tools';
import apiService from '../../services/ApiService';
import { cn } from '../../lib/utils';
import { readClipboardText } from '../../shared/utils/clipboard';

export type VarianceStyle = 'delta' | 'heatmap';
export type Granularity = 'month' | 'quarter' | 'year';

interface OperationalStatementGridProps {
    /** Output (modelized) time_serie fields of the selected item: [name, field]. */
    fields: [string, any][];
    /** Paired operational input item; realized values live in operationalItem.fields[name]. */
    operationalItem: any | null;
    userRights: any;
    reloadState: () => void;
    granularity: Granularity;
    varianceStyle: VarianceStyle;
    /** Keys ("name:date") edited this session — drives highlight + the parent badge. */
    editedCells: Set<string>;
    onEdit: (key: string) => void;
}

/** A display column: one native month, or an aggregate of several. */
interface StatementColumn {
    key: string;
    members: { bop: string; eop: string }[];
    label: string;
    sub: string;
    native: boolean; // true => a single native period
    isTotal?: boolean; // the read-only "All periods" total column
}

const DATE_KEY = (e: any): string | null => (e?.date?.value ?? null);

/** Distinct native periods across all fields, sorted ascending by beginning-of-period. */
function getNativePeriods(fields: [string, any][]): { bop: string; eop: string }[] {
    const map = new Map<string, { bop: string; eop: string }>();
    for (const [, field] of fields) {
        const data = field?.currentValue?.value?.data;
        if (!Array.isArray(data)) continue;
        for (const e of data) {
            const bop = DATE_KEY(e);
            if (bop == null) continue;
            if (!map.has(bop)) map.set(bop, { bop, eop: e?.end_of_period?.value ?? bop });
        }
    }
    return [...map.values()].sort((a, b) => (a.bop < b.bop ? -1 : a.bop > b.bop ? 1 : 0));
}

/** Build display columns for the chosen granularity. Editing is only ever enabled on native months. */
function buildColumns(
    native: { bop: string; eop: string }[],
    gran: Granularity,
    dateFmt: string,
): StatementColumn[] {
    if (gran === 'month') {
        return native.map((p) => ({
            key: p.bop,
            members: [p],
            label: dayjs(p.bop).format('MMM YYYY'),
            sub: dayjs(p.eop).format(dateFmt),
            native: true,
        }));
    }

    const groups = new Map<string, { bop: string; eop: string }[]>();
    for (const p of native) {
        const d = dayjs(p.eop || p.bop);
        const year = d.year();
        const groupKey = gran === 'year' ? `${year}` : `${year}-Q${Math.floor(d.month() / 3) + 1}`;
        if (!groups.has(groupKey)) groups.set(groupKey, []);
        groups.get(groupKey)!.push(p);
    }

    return [...groups.entries()].map(([groupKey, members]) => {
        const first = members[0];
        const last = members[members.length - 1];
        const d = dayjs(last.eop || last.bop);
        const range = `${dayjs(first.bop).format('MMM')}–${dayjs(last.eop).format('MMM')}`;
        return {
            key: groupKey,
            members,
            label: gran === 'year' ? `FY ${d.year()}` : `Q${Math.floor(d.month() / 3) + 1} ${d.year()}`,
            sub: range,
            native: false,
        };
    });
}

/** Per-month value to write for a cell covering `n` months: amounts split evenly, percentages set
 *  on each month (so the column's mean stays the entered value). Mirrors `aggregate`'s sum/mean. */
function perMonth(out: number, n: number, isPercentage: boolean): number {
    return isPercentage ? out : out / n;
}

/** Aggregate a set of period values: sum for amounts, mean for percentages. null when none present. */
function aggregate(values: (number | null | undefined)[], isPercentage: boolean): number | null {
    const present = values.filter((v): v is number => v != null && !Number.isNaN(v));
    if (present.length === 0) return null;
    const total = present.reduce((a, b) => a + b, 0);
    return isPercentage ? total / present.length : total;
}

/** Map beginning-of-period date -> value, from a (modelized or realized) time_serie field.
 *  Output fields expose data on `currentValue` (and `value`, same ref); input fields only on `value`. */
function valueByDate(data: any): Map<string, number | null> {
    const map = new Map<string, number | null>();
    const arr = data?.currentValue?.value?.data ?? data?.value?.value?.data;
    if (Array.isArray(arr)) {
        for (const e of arr) {
            const bop = DATE_KEY(e);
            if (bop != null) map.set(bop, e?.value?.value ?? null);
        }
    }
    return map;
}

export default function OperationalStatementGrid({
    fields,
    operationalItem,
    userRights,
    reloadState,
    granularity,
    varianceStyle,
    editedCells,
    onEdit,
}: OperationalStatementGridProps) {
    const userSettings = useAppStore((s) => s.userSettings);
    // `dates` holds every native month the cell covers (one for a month column, three for a
    // quarter, twelve for a year) so a value entered at any granularity can be split across them.
    const [editingCell, setEditingCell] = useState<{ name: string; bop: string; dates: string[]; isPct: boolean; seed: string } | null>(null);
    const [editValue, setEditValue] = useState('');

    const dateFmt = DATESYSTEM_TO_FORMAT[userSettings.dateSystem as string] || 'DD/MM/YYYY';
    const precision = userSettings.roundPrecision ?? 2;
    const locale = userSettings.numberFormattingLocale;

    const native = useMemo(() => getNativePeriods(fields), [fields]);
    const columns = useMemo(() => buildColumns(native, granularity, dateFmt), [native, granularity, dateFmt]);

    // Editing works at any granularity: amounts entered on a quarter/year are split evenly
    // across the underlying months (the DB is monthly), percentages are set on each month.
    const canEditBase = Boolean(userRights?.right_to_write);

    const formatNumber = useCallback(
        (field: any, value: number | null | undefined): string => {
            if (value == null || Number.isNaN(value)) return '—';
            const raw = field.is_percentage ? value * 100 : value;
            const suffix = field.is_percentage ? ' %' : '';
            return (
                round(raw, precision).toLocaleString(locale, { maximumFractionDigits: precision }) + suffix
            );
        },
        [locale, precision],
    );

    const startEdit = useCallback(
        (name: string, dates: string[], value: number | null, isPct: boolean) => {
            // Seed in display space: percentage fields are stored as a fraction but shown ×100.
            const seed = value == null ? '' : String(isPct ? round(value * 100, precision) : value);
            setEditingCell({ name, bop: dates[0], dates, isPct, seed });
            setEditValue(seed);
        },
        [precision],
    );

    const commitEdit = useCallback(() => {
        if (!editingCell) return;
        const { name, dates, isPct, seed } = editingCell;
        const opField = operationalItem?.fields?.[name];
        const trimmed = editValue.replace(/\s/g, '').trim();

        // No change → close without writing. This no-change guard matters at quarter/year (an
        // unchanged aggregate would otherwise flatten the months) and means a stray click on an
        // unfilled cell won't turn the prefilled model value into a "real" actual.
        if (!opField || trimmed === seed.replace(/\s/g, '').trim()) {
            setEditingCell(null);
            return;
        }

        const data = opField.value?.value?.data;
        const findElem = (d: string) => data?.find((e: any) => e.date.value === d);

        // Write `per` (or null to clear) to each month the cell covers, optimistically once the
        // server accepts the batch. A 1-month cell uses the single-elem endpoint; aggregates the batch.
        const writeAll = (per: number | null) => {
            const hasActual = dates.some((d) => { const e = findElem(d); return e && e.value.value != null; });
            // Clearing an already-unfilled cell is a no-op (avoid a redundant null write).
            if (per === null && !hasActual) {
                setEditingCell(null);
                return;
            }
            const apply = () => {
                dates.forEach((d) => {
                    const e = findElem(d);
                    if (e) e.value.value = per;
                    onEdit(`${name}:${d}`);
                });
                reloadState();
            };
            const request =
                dates.length === 1
                    ? apiService.putOperationalOutputTimeSerieElem(opField.id, dates[0], per)
                    : apiService.putOperationalTimeSerieElems(opField.id, dates.map((d) => ({ date: d, value: per })));
            request.then((response: any) => {
                if (response && response.ok) apply();
            });
        };

        if (trimmed === '') {
            // Blank → clear the realized actuals (null) so the cell reverts to unfilled → model.
            writeAll(null);
        } else {
            const parsed = revertLocaleString(trimmed, locale);
            const out = isPct ? parsed / 100 : parsed; // store percentages as a fraction
            writeAll(perMonth(out, dates.length, isPct)); // amounts split across the cell's months
        }
        setEditingCell(null);
    }, [editingCell, editValue, operationalItem, locale, onEdit, reloadState]);

    // Paste (Ctrl/Cmd+V): paste a 2-D block copied from Excel/Sheets, anchored at the editing
    // cell. Tabs separate periods (across →), newlines separate line items (down ↓). Each row
    // maps to the next time-series field; each column to the next displayed period. The pasted
    // value is split across that period's months (amounts ÷ months; percentages set per month),
    // so quarterly/yearly source data lands correctly in the monthly store. Only the realized
    // (input) series is written; percentage fields are stored as a fraction.
    const handlePaste = useCallback(
        (e: React.KeyboardEvent) => {
            if (!((e.ctrlKey || e.metaKey) && e.key === 'v' && editingCell)) return;
            const anchorName = editingCell.name;
            const anchorBop = editingCell.bop;
            readClipboardText().then((text) => {
                if (!text) return;
                // Parse the clipboard into a matrix; tolerate CRLF/CR and a trailing blank row.
                const matrix = text.replace(/\r\n?/g, '\n').split('\n').map((row) => row.split('\t'));
                while (matrix.length && matrix[matrix.length - 1].every((c) => c.trim() === '')) matrix.pop();
                if (matrix.length === 0) {
                    setEditingCell(null);
                    return;
                }

                const anchorFieldIdx = fields.findIndex(([n]) => n === anchorName);
                const anchorColIdx = columns.findIndex((c) => c.members[0].bop === anchorBop);
                setEditingCell(null);
                if (anchorFieldIdx < 0 || anchorColIdx < 0) return;

                // Plan one batch of writes per target field (skipping fields with no realized series).
                const planned: { opField: any; name: string; items: { elem: any; date: string; value: number }[] }[] = [];
                matrix.forEach((rowVals, r) => {
                    const entry = fields[anchorFieldIdx + r];
                    if (!entry) return;
                    const [fname, ffield] = entry;
                    const opField = operationalItem?.fields?.[fname];
                    const data = opField?.value?.value?.data;
                    if (!opField || !Array.isArray(data)) return;
                    const isPct = Boolean(ffield.is_percentage);
                    const items: { elem: any; date: string; value: number }[] = [];
                    rowVals.forEach((raw, c) => {
                        const col = columns[anchorColIdx + c];
                        if (!col) return;
                        const cell = raw.replace(/\s/g, '');
                        if (cell === '') return; // blank cell → leave existing value untouched
                        const parsed = revertLocaleString(cell, locale);
                        if (Number.isNaN(parsed)) return; // skip non-numeric
                        const out = isPct ? parsed / 100 : parsed;
                        const per = perMonth(out, col.members.length, isPct);
                        col.members.forEach((m) => {
                            const elem = data.find((el: any) => el.date.value === m.bop);
                            if (elem) items.push({ elem, date: m.bop, value: per });
                        });
                    });
                    if (items.length) planned.push({ opField, name: fname, items });
                });

                if (planned.length === 0) return;

                // One PUT per field; apply optimistic updates only for batches the server accepts.
                Promise.all(
                    planned.map((p) =>
                        apiService
                            .putOperationalTimeSerieElems(p.opField.id, p.items.map((i) => ({ date: i.date, value: i.value })))
                            .then((resp: any) => ({ p, ok: Boolean(resp && resp.ok) }))
                            .catch(() => ({ p, ok: false })),
                    ),
                ).then((results) => {
                    let applied = false;
                    results.forEach(({ p, ok }) => {
                        if (!ok) return;
                        applied = true;
                        p.items.forEach((i) => {
                            i.elem.value.value = i.value;
                            onEdit(`${p.name}:${i.date}`);
                        });
                    });
                    if (applied) reloadState();
                });
            });
        },
        [editingCell, fields, columns, operationalItem, locale, onEdit, reloadState],
    );

    if (fields.length === 0 || native.length === 0) {
        return (
            <div className="text-sm text-slate-400 py-8 text-center">No time series data available</div>
        );
    }

    /** Editable / read-only value cell. Renders an inline input when this cell is being edited.
     *  `dates` are the native months the cell covers (1 for a month, 3 for a quarter, 12 for a year);
     *  `dates[0]` is the cell's editing identity. */
    const renderEditable = (
        name: string,
        dates: string[],
        value: number | null,
        field: any,
        editable: boolean,
        strong: boolean,
        muted: boolean, // true => value is the model fallback (no actual entered) → greyed
    ) => {
        const bop = dates[0];
        // Guard on `editable`: the Total column reuses the first period's date as its
        // bop, so without this guard editing the first cell would also match the Total
        // cell and render a second focus-stealing input. Only editable cells ever edit.
        const isEditing = editable && editingCell?.name === name && editingCell?.bop === bop;
        if (isEditing) {
            return (
                <input
                    type="text"
                    value={editValue}
                    autoFocus
                    onChange={(ev) => setEditValue(ev.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={(ev) => {
                        if (ev.key === 'Enter') commitEdit();
                        if (ev.key === 'Escape') setEditingCell(null);
                    }}
                    className="w-full text-right bg-white text-sm font-semibold text-slate-900 px-1.5 py-1 rounded-md border border-primary outline-none ring-2 ring-primary/20 whitespace-nowrap"
                />
            );
        }

        const edited = editedCells.has(`${name}:${bop}`);
        const display = value == null ? '—' : formatNumber(field, value);
        // Muted (model fallback) → grey; a freshly-entered actual → accent; otherwise default ink.
        const tone = value == null || muted ? 'text-slate-300' : edited ? 'text-primary' : '';

        if (editable) {
            return (
                <button
                    onClick={() => startEdit(name, dates, value, field.is_percentage)}
                    className={cn(
                        'w-full text-right px-1.5 py-1 rounded-md border border-transparent transition-colors whitespace-nowrap',
                        'hover:border-primary/40 hover:bg-primary/5',
                        strong && 'font-semibold',
                        tone,
                    )}
                >
                    {display}
                </button>
            );
        }
        return <span className={cn('px-1.5 whitespace-nowrap', strong && 'font-semibold', tone)}>{display}</span>;
    };

    /** Delta chip: realized vs modelized, colored by direction. */
    const DeltaChip = ({ field, m, r }: { field: any; m: number | null; r: number | null }) => {
        if (m == null || r == null) return null;
        const delta = r - m;
        if (delta === 0) return <span className="text-[11px] font-medium text-slate-400">—</span>;
        const pos = delta > 0;
        const pct = m !== 0 ? (delta / Math.abs(m)) * 100 : null;
        return (
            <span className={cn('text-[11px] font-bold whitespace-nowrap', pos ? 'text-emerald-600' : 'text-red-600')}>
                {pos ? '▲' : '▼'} {formatNumber(field, Math.abs(delta))}
                {pct != null && (
                    <span className="font-semibold opacity-80"> {Math.abs(pct).toFixed(1)} %</span>
                )}
            </span>
        );
    };

    /** Variance cell — three interchangeable presentations. `r` is the realized value (actual where
     *  entered, model fallback elsewhere); `m` is the full plan. When the cell holds only the model
     *  fallback (nothing entered), show the greyed plan value — there's nothing to compare against. */
    const renderVariance = (
        name: string,
        dates: string[],
        m: number | null,
        r: number | null,
        field: any,
        editable: boolean,
        strong: boolean,
        muted: boolean,
    ) => {
        if (muted) {
            return renderEditable(name, dates, r, field, editable, strong, true);
        }

        if (varianceStyle === 'heatmap') {
            const pct = m != null && m !== 0 && r != null ? ((r - m) / Math.abs(m)) * 100 : null;
            const intensity = pct == null ? 0 : Math.max(-1, Math.min(1, pct / 12));
            // Tint with the same emerald/red as the delta chips for a consistent hue.
            const bg =
                intensity >= 0
                    ? `rgba(5,150,105,${Math.abs(intensity) * 0.16})`
                    : `rgba(220,38,38,${Math.abs(intensity) * 0.16})`;
            const delta = m != null && r != null ? r - m : null;
            return (
                <div className="flex flex-col items-end rounded-md px-2 py-1 my-0.5 whitespace-nowrap" style={{ background: bg }}>
                    {renderEditable(name, dates, r, field, editable, strong, false)}
                    {pct != null && delta != null && delta !== 0 && (
                        <span className={cn('text-[10px] font-bold', delta >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                            {pct >= 0 ? '+' : '−'}
                            {Math.abs(pct).toFixed(0)} %
                        </span>
                    )}
                </div>
            );
        }

        // default: realized headline + modelized reference + delta
        return (
            <div className="flex flex-col items-end leading-tight py-1">
                {renderEditable(name, dates, r, field, editable, strong, false)}
                <span className="flex items-center gap-1.5 whitespace-nowrap pr-1.5">
                    <span className="text-[10px] text-slate-400">vs {formatNumber(field, m)}</span>
                    <DeltaChip field={field} m={m} r={r} />
                </span>
            </div>
        );
    };

    const renderCell = (
        name: string,
        col: StatementColumn,
        modelMap: Map<string, number | null>,
        realMap: Map<string, number | null>,
        field: any,
        strong: boolean,
    ) => {
        const bops = col.members.map((p) => p.bop);
        const isPct = field.is_percentage;
        const fullM = aggregate(bops.map((b) => modelMap.get(b)), isPct);
        const opField = operationalItem?.fields?.[name];
        // Every period column is editable (aggregated values distribute across their months);
        // only the read-only "All periods" total is not.
        const editable = canEditBase && Boolean(opField) && !col.isTotal;

        // Realized = actual where the user entered one (incl. 0), the modelized plan where not yet
        // entered. `muted` (greyed) when the cell holds only the plan, i.e. no actual exists for it.
        const hasActual = (b: string) => { const v = realMap.get(b); return v != null && !Number.isNaN(v); };
        const muted = !bops.some(hasActual);
        const r = aggregate(bops.map((b) => (hasActual(b) ? realMap.get(b) : modelMap.get(b))), isPct);

        // Realized (fallback) vs the FULL plan; the delta still equals Σ(actual − plan) over entered
        // months, since unentered months contribute plan − plan = 0.
        return renderVariance(name, bops, fullM, r, field, editable, strong, muted);
    };

    const colMinWidth = 140;

    const totalCol: StatementColumn = { key: '__total', members: native, label: 'Total', sub: '', native: false, isTotal: true };

    // ONE table in ONE scroll container: the line-item and Total columns are frozen with
    // `position: sticky`, so rows can never desync (a separate-pane layout coerced the data
    // pane into its own vertical scroll). `isolate` scopes the sticky cells' z-index so they
    // can't overlap the page's sticky headers when the panel scrolls vertically.
    return (
        <div className="overflow-x-auto isolate" onKeyDown={handlePaste} tabIndex={0}>
            <table className="w-full border-separate" style={{ borderSpacing: 0 }}>
                <thead>
                    <tr>
                        <th className="sticky left-0 z-20 bg-slate-50 border-b border-r border-slate-200 text-left px-4 py-2.5 min-w-[232px]">
                            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Line item</span>
                        </th>
                        {columns.map((col) => (
                            <th
                                key={col.key}
                                className="bg-slate-50 border-b border-slate-200 text-right px-3 py-2"
                                style={{ minWidth: colMinWidth }}
                            >
                                <div className="text-[13px] font-bold text-slate-700 tabular-nums">{col.label}</div>
                                <div className="text-[10px] font-medium text-slate-400 tabular-nums">{col.sub}</div>
                            </th>
                        ))}
                        <th
                            className="sticky right-0 z-20 bg-slate-50 border-b border-l border-slate-200 text-right px-3 py-2"
                            style={{ minWidth: 150 }}
                        >
                            <div className="text-[13px] font-bold text-slate-700">Total</div>
                            <div className="text-[10px] font-medium text-slate-400">All periods</div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {fields.map(([name, field]) => {
                        const opField = operationalItem?.fields?.[name];
                        const modelMap = valueByDate(field);
                        const realMap = valueByDate(opField);
                        return (
                            <tr key={name} className="group hover:bg-primary/[0.03] transition-colors">
                                <th
                                    scope="row"
                                    className="sticky left-0 z-10 bg-white group-hover:bg-[#fbfcff] border-b border-r border-slate-100 text-left px-4 py-2 font-normal align-middle"
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-slate-800">{field.display_name || snakeToPascal(name)}</span>
                                        {field.compute_error && (
                                            <span className="inline-flex items-center gap-1 text-[10px] text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                                                <span className="material-symbols-outlined text-[12px]">warning</span>
                                                Error
                                            </span>
                                        )}
                                        {!opField && (
                                            <span className="text-[10px] text-slate-400">no realized series</span>
                                        )}
                                    </div>
                                </th>
                                {columns.map((col) => (
                                    <td
                                        key={col.key}
                                        className="border-b border-slate-100 text-right text-sm text-slate-800 px-2 align-middle tabular-nums"
                                    >
                                        {renderCell(name, col, modelMap, realMap, field, false)}
                                    </td>
                                ))}
                                <td className="sticky right-0 z-10 bg-white group-hover:bg-[#fbfcff] border-b border-l border-slate-100 text-right text-sm px-2 align-middle tabular-nums">
                                    {renderCell(name, totalCol, modelMap, realMap, field, true)}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
